import { useCallback, useEffect, useMemo, useState } from "react";
import { CreditCard } from "lucide-react";
import Panel from "../../components/Panel";
import StatCard from "../../components/StatCard";
import { BtnSecondary, EmptyState, FormModal, PageHeader, Pagination } from "../../components/erp";
import { financeAPI } from "../../services/businessErpService";
import { financeStatusClass, formatCents, formatDate, titleCase } from "./financeUtils";
import ShopSelect from "../../../components/ShopSelect";
import { useBusinessAuth } from "../../context/BusinessAuthContext";

function centsToPesoString(value) {
  return value == null ? "" : (Number(value) / 100).toFixed(2);
}

const RECEIVABLES_PAGE_SIZE = 6;

export default function FinanceReceivables() {
  const auth = useBusinessAuth();
  const canRecordPayments = String(auth?.role || "").toUpperCase() === "OWNER" || Boolean(auth?.hasPermission?.("finance:payments:record"));
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", status: "all", kind: "all" });
  const [page, setPage] = useState(1);
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState({ amount: "", reference_no: "", remarks: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await financeAPI.listReceivables(filters);
      setRows(Array.isArray(res?.items) ? res.items : []);
      setSummary({
        ...(res?.summary || {}),
        settled_amount_cents: res?.summary?.collected_amount_cents ?? res?.summary?.settled_amount_cents ?? 0,
      });
    } catch {
      setRows([]);
      setSummary({});
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const kindOptions = useMemo(() => [
    { value: "all", label: "All Receivables" },
    { value: "sales", label: "Sales" },
    { value: "employee", label: "Employee" },
  ], []);

  const totalPages = Math.max(1, Math.ceil(rows.length / RECEIVABLES_PAGE_SIZE));
  const visibleRows = useMemo(() => {
    const start = (page - 1) * RECEIVABLES_PAGE_SIZE;
    return rows.slice(start, start + RECEIVABLES_PAGE_SIZE);
  }, [page, rows]);

  useEffect(() => {
    setPage(1);
  }, [filters.kind, filters.q, filters.status]);

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  const openCollect = (row) => {
    setTarget(row);
    setForm({
      amount: centsToPesoString(row.amountRemainingCents),
      reference_no: "",
      remarks: "",
    });
  };

  const submit = async () => {
    if (!target || saving) return;
    const amountCents = Math.round(Number(form.amount || 0) * 100);
    if (!Number.isFinite(amountCents) || amountCents < 1) {
      alert("Enter a valid collection amount.");
      return;
    }
    if (amountCents > Number(target.amountRemainingCents || 0)) {
      alert("Collection amount cannot exceed the remaining receivable balance.");
      return;
    }
    setSaving(true);
    try {
      await financeAPI.recordCollection({
        targetType: target.sourceSystem,
        targetId: target.id,
        amount_cents: amountCents,
        reference_no: form.reference_no || null,
        remarks: form.remarks || null,
      });
      setTarget(null);
      await load();
    } catch (err) {
      alert(err?.serverMessage || err?.message || "The collection was not recorded.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Finance"
        title="Finance Receivables"
        subtitle="Track what the business is owed from sales-linked and employee-linked receivables."
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard title="Open Balance" value={loading ? "—" : formatCents(summary.open_amount_cents)} icon={CreditCard} />
        <StatCard title="Overdue" value={loading ? "—" : formatCents(summary.overdue_amount_cents)} icon={CreditCard} accent />
        <StatCard title="Collected" value={loading ? "—" : formatCents(summary.settled_amount_cents)} icon={CreditCard} />
      </div>

      <Panel title="Receivables Queue" subtitle="Outstanding collections linked to source business records">
        <div className="mb-4 grid gap-3 md:grid-cols-[1.2fr_0.6fr_0.6fr]">
          <input
            value={filters.q}
            onChange={(e) => setFilters((current) => ({ ...current, q: e.target.value }))}
            placeholder="Search title, debtor, or reference…"
            className="rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100"
          />
          <ShopSelect
            value={filters.status}
            onValueChange={(value) => setFilters((current) => ({ ...current, status: value }))}
            options={["all", "open", "partially_collected", "collected", "overdue", "settled"].map((value) => ({ value, label: titleCase(value) }))}
            ariaLabel="Filter receivables by status"
            align="start"
            minPanelWidth={176}
            triggerClassName="min-h-10 w-full rounded-xl px-3 py-2 text-sm"
            panelClassName="py-1"
          />
          <ShopSelect
            value={filters.kind}
            onValueChange={(value) => setFilters((current) => ({ ...current, kind: value }))}
            options={kindOptions}
            ariaLabel="Filter receivables by type"
            align="start"
            minPanelWidth={176}
            triggerClassName="min-h-10 w-full rounded-xl px-3 py-2 text-sm"
            panelClassName="py-1"
          />
        </div>

        {loading ? (
          <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading receivables…</div>
        ) : !rows.length ? (
          <EmptyState icon={CreditCard} title="No receivables found" description="No receivables match the current filters." />
        ) : (
          <div className="overflow-hidden rounded-2xl border border-neutral-200 dark:border-neutral-800 lg:flex lg:min-h-[calc(100dvh-28rem)] lg:flex-col">
            <div className="overflow-x-auto lg:flex lg:flex-1 lg:flex-col">
            <div className="min-w-[920px] lg:flex lg:flex-1 lg:flex-col">
            <table className="w-full text-left text-sm bg-white dark:bg-neutral-950 lg:h-full lg:flex-1">
              <thead className="bg-neutral-50 dark:bg-neutral-900/60">
                <tr className="border-b border-neutral-200 dark:border-neutral-800">
                  <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Record</th>
                  <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Debtor</th>
                  <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Due</th>
                  <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400 text-right">Remaining</th>
                  <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Status</th>
                  <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
                {visibleRows.map((row) => (
                  <tr key={row.rowKey}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-neutral-900 dark:text-neutral-100">{row.title}</div>
                      <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{titleCase(row.typeLabel)} • {row.referenceLabel}</div>
                    </td>
                    <td className="px-3 py-2 text-neutral-700 dark:text-neutral-300">
                      <div>{row.counterpartyName || "—"}</div>
                      {row.description ? <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{row.description}</div> : null}
                    </td>
                    <td className="px-3 py-2 text-neutral-700 dark:text-neutral-300">{formatDate(row.dueDate || row.createdAt)}</td>
                    <td className="px-3 py-2 text-right font-medium text-neutral-900 dark:text-neutral-100">{formatCents(row.amountRemainingCents)}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${financeStatusClass(row.statusDisplay)}`}>{titleCase(row.statusDisplay)}</span>
                    </td>
                    <td className="px-3 py-2">
                      {row.manualActionAllowed && canRecordPayments ? (
                        <button
                          onClick={() => openCollect(row)}
                          className="rounded-lg border border-neutral-200 px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-900"
                        >
                          Collect
                        </button>
                      ) : (
                        <span className="text-xs text-neutral-500 dark:text-neutral-400">
                          {row.manualActionAllowed ? "Needs permission to record payments" : "Synced from sales payment state"}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            </div>
            <div className="flex min-h-[4.25rem] flex-wrap items-center justify-between gap-2 border-t border-neutral-200 bg-neutral-50 px-4 dark:border-neutral-800 dark:bg-neutral-900/60 lg:shrink-0">
              <span className="text-xs text-neutral-500 dark:text-neutral-400">
                Showing {(page - 1) * RECEIVABLES_PAGE_SIZE + 1}-{Math.min(page * RECEIVABLES_PAGE_SIZE, rows.length)} of {rows.length}
              </span>
              <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
          </div>
        )}
      </Panel>

      <FormModal
        open={!!target}
        onClose={() => setTarget(null)}
        title="Record Collection"
        size="sm"
        footer={
          <>
            <BtnSecondary onClick={() => setTarget(null)}>Cancel</BtnSecondary>
            <button onClick={submit} disabled={saving} className="business-primary-action rounded-xl bg-neutral-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-60 dark:bg-neutral-100 dark:text-neutral-900">
              {saving ? "Saving…" : "Record Collection"}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3 text-sm dark:border-neutral-800 dark:bg-neutral-900/50">
            <div className="font-medium text-neutral-900 dark:text-neutral-100">{target?.title}</div>
            <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{target?.counterpartyName || "—"} • Remaining {formatCents(target?.amountRemainingCents)}</div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Amount</label>
            <input type="number" min="0.01" step="0.01" value={form.amount} onChange={(e) => setForm((current) => ({ ...current, amount: e.target.value }))} className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Reference No.</label>
            <input value={form.reference_no} onChange={(e) => setForm((current) => ({ ...current, reference_no: e.target.value }))} className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Remarks</label>
            <textarea rows={3} value={form.remarks} onChange={(e) => setForm((current) => ({ ...current, remarks: e.target.value }))} className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100" />
          </div>
        </div>
      </FormModal>
    </div>
  );
}
